import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import Logo from "@/components/Logo";
import Waves from "@/components/Waves";
import OnboardingPrompt from "@/components/OnboardingPrompt";
import Crest from "@/components/Crest";
import NotificationBell from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Sailboat, Users, FolderLock, Map, MessageSquareWarning,
  Sun, Moon, Menu, LogOut, UserCircle, Shield, Send, ChevronDown, Anchor, Palmtree, PartyPopper, Landmark, MessagesSquare, Sparkles, Cake, BarChart3,
  ArrowLeft, ArrowRight, RotateCw, RefreshCw, Smartphone,
} from "lucide-react";
import { MessagesProvider, useMessages } from "@/context/MessagesContext";
import { NAV_ICONS } from "@/lib/brandImages";
import MemberAvatar from "@/components/MemberAvatar";
import InstallApp, { InstallBanner } from "@/components/InstallApp";
import { toast } from "sonner";

const GUEST_NAV = [
  { to: "/dashboard", label: "IOTA Galleon Mess", icon: LayoutDashboard },
  { to: "/messages", label: "Messages", icon: MessagesSquare, badge: "unread" },
  { to: "/island", label: "The Island", icon: Palmtree },
  { to: "/happy-hour", label: "Happy Hour", icon: PartyPopper },
];
const MEMBER_NAV = [
  { to: "/dashboard", label: "IOTA Galleon Mess", icon: LayoutDashboard },
  { to: "/messages", label: "Messages", icon: MessagesSquare, badge: "unread" },
  { to: "/island", label: "The Island", icon: Palmtree },
  { to: "/happy-hour", label: "Happy Hour", icon: PartyPopper },
  { to: "/office", label: "Administrative Office", icon: Landmark },
  { to: "/oracle", label: "Oracle Boot", icon: Sparkles },
  { to: "/fliers", label: "Birthday Fliers", icon: Cake },
  { to: "/population", label: "Membership Dashboard", icon: BarChart3 },
  { to: "/frigates", label: "Frigates", icon: Sailboat },
  { to: "/directory", label: "Directory", icon: Users },
  { to: "/library", label: "Library", icon: FolderLock },
  { to: "/progress", label: "Progress Map", icon: Map },
  { to: "/feedback", label: "Feedback", icon: MessageSquareWarning },
];
const OFFICER_NAV = [
  { to: "/invite", label: "Invite", icon: Send },
  { to: "/command", label: "Command Deck", icon: Shield },
];

const roleLabel = { guest: "Visiting Brother", member: "Member", officer: "Officer", super_admin: "Super Admin" };
const IDLE_MS = 15 * 60 * 1000;
const AUTO_REFRESH_MS = 60 * 1000;
const AUTO_REFRESH_PAGES = ["/dashboard", "/happy-hour", "/frigates", "/directory", "/library", "/progress", "/population", "/command", "/office", "/feedback"];

// Only refresh read-mostly pages silently, and never while it could interrupt the brother (editing, dialogs, media)
function safeToRefresh(pathname) {
  if (!AUTO_REFRESH_PAGES.some((p) => pathname.startsWith(p))) return false;
  if (document.querySelector('[role="dialog"], [data-no-auto-refresh]')) return false;
  const a = document.activeElement;
  if (a && (a.tagName === "INPUT" || a.tagName === "TEXTAREA" || a.isContentEditable)) return false;
  const typed = [...document.querySelectorAll("main input, main textarea")].some((el) => !["file", "checkbox", "radio", "range"].includes(el.type) && el.value);
  if (typed) return false;
  return ![...document.querySelectorAll("main video, main audio")].some((m) => !m.paused && !m.muted);
}

function NavToolbar({ onRefresh, spinning }) {
  const navigate = useNavigate();
  return (
    <div className="flex items-center gap-0.5 rounded-full border border-[hsl(var(--gold))]/25 bg-background/60 p-0.5" data-testid="nav-toolbar">
      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full text-[hsl(var(--gold))]" onClick={() => navigate(-1)} aria-label="Back" title="Back" data-testid="nav-back-button"><ArrowLeft className="h-4 w-4" /></Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full text-[hsl(var(--gold))]" onClick={() => navigate(1)} aria-label="Forward" title="Forward" data-testid="nav-forward-button"><ArrowRight className="h-4 w-4" /></Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full text-[hsl(var(--gold))]" onClick={onRefresh} aria-label="Refresh" title="Refresh this page" data-testid="refresh-button"><RotateCw className={`h-4 w-4 ${spinning ? "animate-spin" : ""}`} /></Button>
    </div>
  );
}

function NavItems({ onNavigate }) {
  const { hasRole, user } = useAuth();
  const { unread } = useMessages();
  const items = user?.role === "guest" ? GUEST_NAV : [...MEMBER_NAV, ...(hasRole("officer") ? OFFICER_NAV : [])];
  return (
    <nav className="flex flex-col gap-1 px-3">
      {items.map(({ to, label, icon: Icon, badge }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onNavigate}
          data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-600 transition-colors ${
              isActive
                ? "bg-gradient-to-r from-[hsl(var(--gold))]/20 to-transparent text-[hsl(var(--gold))] shadow-[inset_3px_0_0_hsl(var(--gold))]"
                : "text-muted-foreground hover:bg-[hsl(var(--water))]/10 hover:text-foreground"
            }`
          }
        >
          {NAV_ICONS[to] ? <img src={NAV_ICONS[to]} alt="" className="h-[24px] w-[24px] rounded-full object-cover shadow-sm ring-1 ring-[hsl(var(--gold))]/50 transition-transform group-hover:scale-110" data-testid={`nav-icon-${label.toLowerCase().replace(/\s+/g, "-")}`} /> : <Icon className="h-[18px] w-[18px]" />}
          <span className="flex-1">{label}</span>
          {badge === "unread" && unread > 0 && <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[hsl(var(--gold))] px-1.5 text-[0.6rem] font-700 text-[hsl(var(--primary-foreground))]" data-testid="nav-messages-unread">{unread > 99 ? "99+" : unread}</span>}
        </NavLink>
      ))}
    </nav>
  );
}

function Sidebar() {
  return (
    <aside className="hidden w-64 shrink-0 border-r border-[hsl(var(--gold))]/15 bg-card/40 lg:flex lg:flex-col">
      <div className="flex h-16 items-center border-b border-[hsl(var(--gold))]/15 px-5">
        <Logo compact />
        <span className="ml-3 font-display text-sm font-700 gold-text">IOTA GALLEON</span>
      </div>
      <div className="flex-1 overflow-y-auto py-4"><NavItems /></div>
      <div className="border-t border-[hsl(var(--gold))]/15 p-4 text-[0.65rem] text-muted-foreground">
        <Anchor className="mb-1 h-4 w-4 text-[hsl(var(--gold))]" />
        Confidential — Members Only
      </div>
    </aside>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const idleRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(() => localStorage.getItem("autoRefresh") !== "off");
  const refresh = () => { setSpinning(true); setRefreshKey((k) => k + 1); setTimeout(() => setSpinning(false), 700); };
  const toggleAuto = () => { const v = !autoRefresh; setAutoRefresh(v); localStorage.setItem("autoRefresh", v ? "on" : "off"); toast.info(v ? "Auto-refresh on — pages stay up to date" : "Auto-refresh off"); };

  // Auto-refresh: every minute while visible, and when the brother returns to the tab
  useEffect(() => {
    if (!autoRefresh) return;
    let hiddenAt = 0;
    const tick = () => { if (document.visibilityState === "visible" && safeToRefresh(location.pathname)) setRefreshKey((k) => k + 1); };
    const t = setInterval(tick, AUTO_REFRESH_MS);
    const vis = () => { if (document.visibilityState === "hidden") hiddenAt = Date.now(); else if (hiddenAt && Date.now() - hiddenAt > 20000) tick(); };
    document.addEventListener("visibilitychange", vis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", vis); };
  }, [autoRefresh, location.pathname]);

  // Session timeout on inactivity
  useEffect(() => {
    const reset = () => {
      if (idleRef.current) clearTimeout(idleRef.current);
      idleRef.current = setTimeout(async () => {
        await logout();
        toast.info("Session expired due to inactivity. Please sign in again.");
        navigate("/login");
      }, IDLE_MS);
    };
    const evts = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => { evts.forEach((e) => window.removeEventListener(e, reset)); if (idleRef.current) clearTimeout(idleRef.current); };
  }, [logout, navigate]);

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <MessagesProvider>
    <div className="relative flex min-h-screen bg-background sea-bg">
      <Waves />
      <OnboardingPrompt />
      <Sidebar />
      <div className="relative z-10 flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-[hsl(var(--gold))]/20 bg-background/85 px-4 backdrop-blur-xl lg:px-8">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <div className="flex h-16 items-center border-b border-[hsl(var(--gold))]/15 px-5"><Logo /></div>
                <div className="py-4"><NavItems onNavigate={() => setOpen(false)} /></div>
              </SheetContent>
            </Sheet>
            <div className="lg:hidden"><Logo compact /></div>
            <NavToolbar onRefresh={refresh} spinning={spinning} />
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <Crest position={user?.position} size={26} className="hidden sm:inline-flex" />
            <NotificationBell />
            <Badge
              variant="outline"
              className="hidden border-[hsl(var(--gold))]/40 text-[hsl(var(--gold))] sm:inline-flex"
              data-testid="role-badge"
            >
              {user?.position && user.position !== "Member" ? user.position : (roleLabel[user?.role] || "Member")}
            </Badge>
            <Button
              variant="ghost" size="icon" onClick={toggle}
              data-testid="theme-toggle-button" aria-label="Toggle theme"
              className="text-[hsl(var(--gold))]"
            >
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full border border-[hsl(var(--gold))]/30 py-1 pl-1 pr-2 transition-colors hover:border-[hsl(var(--gold))]/70" data-testid="profile-menu-button">
                  <MemberAvatar name={user?.name} photo={user?.photo_path} className="h-8 w-8" testid="header-avatar" />
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-700">{user?.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <UserCircle className="mr-2 h-4 w-4" /> Profile & Security
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setInstallOpen(true)} data-testid="menu-install-app">
                  <Smartphone className="mr-2 h-4 w-4" /> Install app on my phone
                </DropdownMenuItem>
                <DropdownMenuItem onClick={toggleAuto} data-testid="auto-refresh-toggle">
                  <RefreshCw className="mr-2 h-4 w-4" /> Auto-refresh: {autoRefresh ? "On" : "Off"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={doLogout} data-testid="menu-logout" className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main key={`${location.pathname}#${refreshKey}`} className="animate-fade-up flex-1 p-4 lg:p-8" data-testid="main-content" data-refresh={refreshKey}>
          {location.pathname === "/dashboard" && <InstallBanner onOpen={() => setInstallOpen(true)} />}
          {children}
        </main>
      </div>
      <InstallApp open={installOpen} onClose={() => setInstallOpen(false)} />
    </div>
    </MessagesProvider>
  );
}
